import React from 'react';
import { Alert, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle, faRedo, faComments } from '@fortawesome/free-solid-svg-icons';
import './MessageErrorBoundary.css';

/**
 * Specialized error boundary for messaging-specific errors
 * Provides messaging-context fallback UI and recovery options
 */
class MessageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorType: null
    };
  }

  static getDerivedStateFromError(error) {
    // Determine error type based on error message
    let errorType = 'general';
    
    if (error.message) {
      const message = error.message.toLowerCase();
      if (message.includes('websocket') || message.includes('connection')) {
        errorType = 'connection';
      } else if (message.includes('network') || message.includes('fetch')) {
        errorType = 'network';
      } else if (message.includes('unauthorized') || message.includes('forbidden')) {
        errorType = 'auth';
      } else if (message.includes('message') || message.includes('thread')) {
        errorType = 'messaging';
      }
    }

    return { 
      hasError: true,
      errorType
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('MessageErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Report error with messaging context
    this.reportMessagingError(error, errorInfo);
  }

  reportMessagingError = (error, errorInfo) => {
    const errorReport = {
      context: 'messaging',
      component: this.props.componentName || 'unknown',
      message: error.toString(),
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      errorType: this.state.errorType,
      userAgent: navigator.userAgent
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group('Messaging Error Report');
      console.error('Error Type:', this.state.errorType);
      console.error('Component:', this.props.componentName);
      console.error('Error:', error);
      console.error('Component Stack:', errorInfo.componentStack);
      console.groupEnd();
    }

    // TODO: Send to error tracking service with messaging context
    console.log('Messaging error report:', errorReport);
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorType: null
    });

    // Call optional onReset callback
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleRetry = () => {
    // Call optional onRetry callback
    if (this.props.onRetry) {
      this.props.onRetry();
    }
    
    this.handleReset();
  };

  getErrorMessage = () => {
    const { errorType } = this.state;
    
    switch (errorType) {
      case 'connection':
        return {
          title: 'Connection Error',
          message: 'Unable to establish real-time connection. Messages may be delayed.',
          icon: faComments
        };
      case 'network':
        return {
          title: 'Network Error',
          message: 'Unable to reach the messaging service. Please check your internet connection.',
          icon: faExclamationCircle
        };
      case 'auth':
        return {
          title: 'Authentication Error',
          message: 'Your session may have expired. Please try signing in again.',
          icon: faExclamationCircle
        };
      case 'messaging':
        return {
          title: 'Messaging Error',
          message: 'An error occurred while processing your message. Please try again.',
          icon: faComments
        };
      default:
        return {
          title: 'Something went wrong',
          message: 'An unexpected error occurred in the messaging system.',
          icon: faExclamationCircle
        };
    }
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const errorMessage = this.getErrorMessage();
      const { compact } = this.props;

      // Compact error display for inline components
      if (compact) {
        return (
          <Alert variant="warning" className="message-error-compact">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={errorMessage.icon} className="me-2" />
                <span>{errorMessage.message}</span>
              </div>
              <Button 
                variant="outline-warning" 
                size="sm"
                onClick={this.handleRetry}
              >
                <FontAwesomeIcon icon={faRedo} className="me-1" />
                Retry
              </Button>
            </div>
          </Alert>
        );
      }

      // Full error display
      return (
        <div className="message-error-boundary">
          <Alert variant="warning" className="message-error-alert">
            <div className="message-error-icon">
              <FontAwesomeIcon icon={errorMessage.icon} size="2x" />
            </div>
            <Alert.Heading>{errorMessage.title}</Alert.Heading>
            <p>{errorMessage.message}</p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="message-error-details">
                <summary>Technical Details (Development Only)</summary>
                <pre className="error-message">
                  {this.state.error.toString()}
                </pre>
                {this.state.errorInfo && (
                  <pre className="error-stack">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </details>
            )}

            <div className="message-error-actions">
              <Button 
                variant="primary" 
                onClick={this.handleRetry}
                className="me-2"
              >
                <FontAwesomeIcon icon={faRedo} className="me-2" />
                Try Again
              </Button>
              {this.props.showResetButton && (
                <Button 
                  variant="outline-secondary" 
                  onClick={this.handleReset}
                >
                  Reset
                </Button>
              )}
            </div>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}

export default MessageErrorBoundary;
